import { useState, useEffect } from "react";
import {
  Flex,
  Dialog,
  Heading,
  Content,
  TextField,
  ButtonGroup,
  Button,
  Picker,
  Item,
  Key,
  ActionButton,
  DialogTrigger,
  ColorSwatchPicker,
  ColorSwatch,
} from "@adobe/react-spectrum";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  $createParagraphNode,
  $isTextNode,
  $getNodeByKey,
  TextFormatType,
  $createTextNode,
  $insertNodes,
} from "lexical";
import { $setBlocksType } from "@lexical/selection";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $createHeadingNode } from "@lexical/rich-text";
import TextAlignCenterIcon from "@spectrum-icons/workflow/TextAlignCenter";
import TextAlignLeftIcon from "@spectrum-icons/workflow/TextAlignLeft";
import TextAlignRightIcon from "@spectrum-icons/workflow/TextAlignRight";
import TextBulletedIcon from "@spectrum-icons/workflow/TextBulleted";
import TextNumberedIcon from "@spectrum-icons/workflow/TextNumbered";
import TextColorIcon from "@spectrum-icons/workflow/TextColor";
import TextStrikethroughIcon from "@spectrum-icons/workflow/TextStrikethrough";
import TagItalicIcon from "@spectrum-icons/workflow/TagItalic";
import TagBoldIcon from "@spectrum-icons/workflow/TagBold";
import TagUnderlineIcon from "@spectrum-icons/workflow/TagUnderline";
import LinkIcon from "@spectrum-icons/workflow/Link";

export default function Toolbar({
  selectedNoteId,
}: {
  selectedNoteId: string;
}) {
  const [editor] = useLexicalComposerContext();
  const [linkValue, setLinkValue] = useState("");

  const [currentTextColor, setCurrentTextColor] = useState("#000000");
  const [selectedHeading, setSelectedHeading] = useState("Body");
  const [isLinkActive, setIsLinkActive] = useState(false);

  // Track active formatting states
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [textAlignment, setTextAlignment] = useState<
    "left" | "center" | "right"
  >("left");
  const [isInList, setIsInList] = useState(false);
  const [listType, setListType] = useState<"ordered" | "unordered" | null>(
    null
  );

  // Listen for selection changes and update formatting states
  useEffect(() => {
    const removeUpdateListener = editor.registerUpdateListener(
      ({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            setIsBold(selection.hasFormat("bold"));
            setIsItalic(selection.hasFormat("italic"));
            setIsUnderline(selection.hasFormat("underline"));
            setIsStrikethrough(selection.hasFormat("strikethrough"));

            // Check current block type for heading dropdown
            const anchor = selection.anchor;
            const anchorNode = anchor.getNode();
            let blockNode = anchorNode;

            // If we're in a text node, get its parent block
            if (blockNode.getType() === "text") {
              const parentNode = blockNode.getParent();
              if (parentNode) {
                blockNode = parentNode;
              }
            }

            if (blockNode) {
              const blockType = blockNode.getType();
              if (blockType === "heading") {
                const tag = (blockNode as any).getTag();
                if (tag === "h1") {
                  setSelectedHeading("Heading 1");
                } else if (tag === "h2") {
                  setSelectedHeading("Heading 2");
                } else if (tag === "h3") {
                  setSelectedHeading("Heading 3");
                }
              } else {
                setSelectedHeading("Body");
              }
            }

            // Check alignment
            const parent = anchorNode.getParent();
            if (parent) {
              const format = parent.getFormat();
              if (format === 2) {
                // center
                setTextAlignment("center");
              } else if (format === 3) {
                // right
                setTextAlignment("right");
              } else {
                setTextAlignment("left");
              }
            }

            // Check current text color
            const nodes = selection.getNodes();
            if (nodes.length > 0) {
              const firstNode = nodes[0];
              if ($isTextNode(firstNode)) {
                const style = firstNode.getStyle();
                if (style && style.includes("color:")) {
                  const colorMatch = style.match(/color:\s*([^;]+)/);
                  if (colorMatch) {
                    setCurrentTextColor(colorMatch[1].trim());
                  }
                }

                // Check if the text is part of a list
                let inList = false;
                let listTypeFound: "ordered" | "unordered" | null = null;

                // Check all nodes in the selection for list items
                const allNodes = selection.getNodes();
                for (const node of allNodes) {
                  const nodeParent = node.getParent();
                  if (nodeParent && nodeParent.getType() === "listitem") {
                    inList = true;
                    const listParent = nodeParent.getParent();
                    if (listParent && listParent.getType() === "list") {
                      const listTag = (listParent as any).getTag();
                      if (listTag === "ol") {
                        listTypeFound = "ordered";
                      } else if (listTag === "ul") {
                        listTypeFound = "unordered";
                      }
                    }
                    break; // Found a list item, no need to check further
                  }
                }

                // Also check the anchor and focus nodes specifically
                if (!inList) {
                  const anchorNode = selection.anchor.getNode();
                  const focusNode = selection.focus.getNode();
                  const anchorParent = anchorNode.getParent();
                  const focusParent = focusNode.getParent();

                  if (anchorParent && anchorParent.getType() === "listitem") {
                    inList = true;
                    const listParent = anchorParent.getParent();
                    if (listParent && listParent.getType() === "list") {
                      const listTag = (listParent as any).getTag();
                      if (listTag === "ol") {
                        listTypeFound = "ordered";
                      } else if (listTag === "ul") {
                        listTypeFound = "unordered";
                      }
                    }
                  } else if (
                    focusParent &&
                    focusParent.getType() === "listitem"
                  ) {
                    inList = true;
                    const listParent = focusParent.getParent();
                    if (listParent && listParent.getType() === "list") {
                      const listTag = (listParent as any).getTag();
                      if (listTag === "ol") {
                        listTypeFound = "ordered";
                      } else if (listTag === "ul") {
                        listTypeFound = "unordered";
                      }
                    }
                  }
                }

                setIsInList(inList);
                setListType(listTypeFound);
              }
            }
          }
        });
      }
    );

    return removeUpdateListener;
  }, [editor]);

  // Reset link value when switching notes
  useEffect(() => {
    setLinkValue("");
    setIsLinkActive(false);
  }, [selectedNoteId]);

  const applyFormat = (type: TextFormatType) => {
    console.log("Applying format:", type);
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, type);
  };

  const setHeading = (level: "h1" | "h2" | "p") => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (selection.isCollapsed()) {
          // For collapsed selection, apply formatting to the current block
          const anchor = selection.anchor;
          const anchorNode = anchor.getNode();
          const parent = anchorNode.getParent();

          if (parent) {
            // Apply the heading to the current block
            if (level === "p") {
              $setBlocksType(selection, () => $createParagraphNode());
            } else {
              $setBlocksType(selection, () => $createHeadingNode(level));
            }
          }
        } else {
          // For text selection, store original boundaries for restoration
          const anchorKey = selection.anchor.key;
          const focusKey = selection.focus.key;
          const anchorOffset = selection.anchor.offset;
          const focusOffset = selection.focus.offset;

          // Apply the heading to selected blocks
          if (level === "p") {
            $setBlocksType(selection, () => $createParagraphNode());
          } else {
            $setBlocksType(selection, () => $createHeadingNode(level));
          }

          // Restore the original selection to prevent expansion
          try {
            const anchorNode = $getNodeByKey(anchorKey);
            const focusNode = $getNodeByKey(focusKey);

            if (anchorNode && focusNode) {
              selection.anchor.set(anchorKey, anchorOffset, "text");
              selection.focus.set(focusKey, focusOffset, "text");
            }
          } catch (error) {
            // If restoration fails, continue with current selection
            console.log("Selection restoration failed:", error);
          }
        }
      }
    });
  };

  const handleHeadingChange = (key: Key | null) => {
    if (!key) return;
    const keyString = key.toString();
    setSelectedHeading(keyString);
    switch (keyString) {
      case "Body":
        setHeading("p");
        break;
      case "Heading 1":
        setHeading("h1");
        break;
      case "Heading 2":
        setHeading("h2");
        break;
      default:
        setHeading("p");
    }
  };

  const insertList = (ordered: boolean) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Store the original selection boundaries
        const anchorKey = selection.anchor.key;
        const focusKey = selection.focus.key;
        const anchorOffset = selection.anchor.offset;
        const focusOffset = selection.focus.offset;

        // Check if the selection is already in a list
        const anchor = selection.anchor;
        const focus = selection.focus;

        // Check if we're already in a list by looking at the current selection
        let isInList = false;
        const nodes = selection.getNodes();

        for (const node of nodes) {
          const parent = node.getParent();
          if (parent && parent.getType() === "listitem") {
            isInList = true;
            break;
          }
        }

        // Also check if the anchor and focus nodes are in list items
        if (!isInList) {
          const anchorParent = anchor.getNode().getParent();
          const focusParent = focus.getNode().getParent();
          if (anchorParent && anchorParent.getType() === "listitem") {
            isInList = true;
          } else if (focusParent && focusParent.getType() === "listitem") {
            isInList = true;
          }
        }

        if (isInList) {
          // If already in a list, remove the list formatting
          console.log("Removing list formatting");
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        } else {
          // If not in a list, insert a new list
          console.log("Adding list formatting");
          editor.dispatchCommand(
            ordered
              ? INSERT_ORDERED_LIST_COMMAND
              : INSERT_UNORDERED_LIST_COMMAND,
            undefined
          );
        }

        // Restore the original selection to prevent expansion
        try {
          const anchorNode = $getNodeByKey(anchorKey);
          const focusNode = $getNodeByKey(focusKey);

          if (anchorNode && focusNode) {
            selection.anchor.set(anchorKey, anchorOffset, "text");
            selection.focus.set(focusKey, focusOffset, "text");
          }
        } catch (error) {
          // If restoration fails, continue with current selection
          console.log("Selection restoration failed:", error);
        }
      }
    });
  };

  const applyAlignment = (alignment: "left" | "center" | "right") => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Store the original selection boundaries
        const anchorKey = selection.anchor.key;
        const focusKey = selection.focus.key;
        const anchorOffset = selection.anchor.offset;
        const focusOffset = selection.focus.offset;

        // Ensure we have a valid selection
        if (selection.isCollapsed()) {
          // If no text is selected, select the current block
          const anchor = selection.anchor;
          const anchorNode = anchor.getNode();
          const parent = anchorNode.getParent();

          if (parent) {
            // Select the entire block
            parent.select();
          }
        }

        // Apply the alignment
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, alignment);

        // Restore the original selection to prevent expansion
        try {
          const anchorNode = $getNodeByKey(anchorKey);
          const focusNode = $getNodeByKey(focusKey);

          if (anchorNode && focusNode) {
            selection.anchor.set(anchorKey, anchorOffset, "text");
            selection.focus.set(focusKey, focusOffset, "text");
          }
        } catch (error) {
          // If restoration fails, continue with current selection
          console.log("Selection restoration failed:", error);
        }
      }
    });
  };

  const applyTextColor = (color: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Don't apply color to collapsed selection as it would affect entire text node
        if (selection.isCollapsed()) {
          return;
        }

        // Get the selected nodes and handle partial selections properly
        const nodes = selection.getNodes();
        const anchorNode = selection.anchor.getNode();
        const focusNode = selection.focus.getNode();
        const anchorOffset = selection.anchor.offset;
        const focusOffset = selection.focus.offset;

        // Helper function to merge styles
        const mergeStyles = (
          existingStyle: string,
          newColor: string
        ): string => {
          if (!existingStyle) {
            return `color: ${newColor};`;
          }

          // Remove existing color style if present
          const styleWithoutColor = existingStyle
            .replace(/color:\s*[^;]+;?/gi, "")
            .trim();
          const separator =
            styleWithoutColor && !styleWithoutColor.endsWith(";") ? "; " : "";
          return styleWithoutColor + separator + `color: ${newColor};`;
        };

        // Handle the case where selection is within a single text node
        if (anchorNode === focusNode && $isTextNode(anchorNode)) {
          const startOffset = Math.min(anchorOffset, focusOffset);
          const endOffset = Math.max(anchorOffset, focusOffset);

          // Only proceed if we're selecting part of the text, not the entire node
          if (
            startOffset > 0 ||
            endOffset < anchorNode.getTextContent().length
          ) {
            const textContent = anchorNode.getTextContent();
            const beforeText = textContent.substring(0, startOffset);
            const selectedText = textContent.substring(startOffset, endOffset);
            const afterText = textContent.substring(endOffset);

            // Get the current style
            const currentStyle = anchorNode.getStyle();

            // Create new nodes
            const beforeNode = beforeText ? $createTextNode(beforeText) : null;
            const selectedNode = $createTextNode(selectedText);
            const afterNode = afterText ? $createTextNode(afterText) : null;

            // Apply existing style to all nodes, but add color only to selected node
            if (beforeNode && currentStyle) {
              beforeNode.setStyle(currentStyle);
            }
            if (afterNode && currentStyle) {
              afterNode.setStyle(currentStyle);
            }

            // Apply color to selected node with merged styles
            const newStyle = mergeStyles(currentStyle, color);
            selectedNode.setStyle(newStyle);

            // Replace the original node with the split nodes
            const nodesToInsert = [beforeNode, selectedNode, afterNode].filter(
              (node): node is NonNullable<typeof node> => node !== null
            );
            if (nodesToInsert.length > 0) {
              anchorNode.replace(nodesToInsert[0]);
              if (nodesToInsert.length > 1) {
                for (let i = 1; i < nodesToInsert.length; i++) {
                  nodesToInsert[i - 1].insertAfter(nodesToInsert[i]);
                }
              }
            }

            // Restore selection to the colored text
            selectedNode.select(0, selectedText.length);
            return;
          }
        }

        // Handle multi-node selections or full node selections
        nodes.forEach((node) => {
          if ($isTextNode(node)) {
            const currentStyle = node.getStyle();
            const newStyle = mergeStyles(currentStyle, color);
            node.setStyle(newStyle);
          }
        });
      }
    });
  };

  const handleLinkToggle = () => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        let currentUrl = "";

        // Check if cursor is positioned on a link (even if no text is selected)
        if (selection.isCollapsed()) {
          // Cursor is positioned, check if it's on a link
          const anchor = selection.anchor;
          const anchorNode = anchor.getNode();
          
          // If we're in a text node, check its parent
          if ($isTextNode(anchorNode)) {
            const parent = anchorNode.getParent();
            if (parent && parent.getType() === "link") {
              currentUrl = (parent as any).getURL?.() || "";
            }
          }
        } else {
          // Text is selected, check if the selection is already a link
          const nodes = selection.getNodes();

          for (const node of nodes) {
            if ($isTextNode(node)) {
              const parent = node.getParent();
              if (parent && parent.getType() === "link") {
                currentUrl = (parent as any).getURL?.() || "";
                break;
              }
            }
          }
        }

        setLinkValue(currentUrl);
        setIsLinkActive(true);
      }
    });
  };

  const validateAndApplyLink = (url: string) => {
    let validUrl = url.trim();

    if (!validUrl) {
      setIsLinkActive(false);
      setLinkValue("");
      return true;
    }

    // Add protocol if missing
    if (!validUrl.match(/^https?:\/\//)) {
      validUrl = "https://" + validUrl;
    }

    // Basic URL validation
    try {
      new URL(validUrl);

      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          if (selection.isCollapsed()) {
            // No text selected, insert the URL as both text and link
            const textNode = $createTextNode(validUrl);
            $insertNodes([textNode]);
            // Select the inserted text and make it a link
            textNode.select();
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, validUrl);
          } else {
            // Text is selected, apply link to selection
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, validUrl);
          }
        }
      });

      setIsLinkActive(false);
      setLinkValue("");
      return true;
    } catch (error) {
      console.error("Invalid URL:", validUrl, error);
      return false;
    }
  };

  return (
    <>
      <Flex alignItems="center" UNSAFE_className="mynotes-toolbar">
        <Picker
          selectedKey={selectedHeading}
          onSelectionChange={handleHeadingChange}
          marginEnd="size-100"
        >
          <Item key="Body">Body</Item>
          <Item key="Heading 1">Heading 1</Item>
          <Item key="Heading 2">Heading 2</Item>
        </Picker>
        <ActionButton
          isQuiet={!isBold}
          onPress={() => applyFormat("bold")}
          aria-label="Bold"
        >
          <TagBoldIcon size="S" />
        </ActionButton>
        <ActionButton
          isQuiet={!isItalic}
          onPress={() => applyFormat("italic")}
          aria-label="Italic"
        >
          <TagItalicIcon size="S" />
        </ActionButton>
        <ActionButton
          isQuiet={!isUnderline}
          onPress={() => applyFormat("underline")}
          aria-label="Underline"
        >
          <TagUnderlineIcon size="S" />
        </ActionButton>
        <ActionButton
          isQuiet={!isStrikethrough}
          onPress={() => applyFormat("strikethrough")}
          aria-label="Strikethrough"
        >
          <TextStrikethroughIcon size="S" />
        </ActionButton>
        <span className="rt-sep" />

        <DialogTrigger type="popover">
          <ActionButton isQuiet aria-label="Text color">
            <TextColorIcon size="S" />
          </ActionButton>
          <Dialog>
            <Heading>Text Color</Heading>
            <Content>
              <ColorSwatchPicker
                value={currentTextColor}
                onChange={(color) => {
                  const colorString = color.toString();
                  setCurrentTextColor(colorString);
                  applyTextColor(colorString);
                  // Close the popover after color selection
                  // The DialogTrigger will handle this automatically
                }}
              >
                <ColorSwatch color="#000000" />
                <ColorSwatch color="#FF0000" />
                <ColorSwatch color="#00FF00" />
                <ColorSwatch color="#0000FF" />
                <ColorSwatch color="#FFA500" />
                <ColorSwatch color="#800080" />
                <ColorSwatch color="#008080" />
                <ColorSwatch color="#A52A2A" />
                <ColorSwatch color="#808080" />
              </ColorSwatchPicker>
            </Content>
          </Dialog>
        </DialogTrigger>
        <span className="rt-sep" />
        <ActionButton
          isQuiet={textAlignment !== "left"}
          onPress={() => applyAlignment("left")}
          aria-label="Align left"
        >
          <TextAlignLeftIcon size="S" />
        </ActionButton>
        <ActionButton
          isQuiet={textAlignment !== "center"}
          onPress={() => applyAlignment("center")}
          aria-label="Align center"
        >
          <TextAlignCenterIcon size="S" />
        </ActionButton>
        <ActionButton
          isQuiet={textAlignment !== "right"}
          onPress={() => applyAlignment("right")}
          aria-label="Align right"
        >
          <TextAlignRightIcon size="S" />
        </ActionButton>
        <span className="rt-sep" />
        <ActionButton
          isQuiet={!isInList || listType !== "unordered"}
          onPress={() => insertList(false)}
          aria-label="Bullet list"
        >
          <TextBulletedIcon size="S" />
        </ActionButton>
        <ActionButton
          isQuiet={!isInList || listType !== "ordered"}
          onPress={() => insertList(true)}
          aria-label="Numbered list"
        >
          <TextNumberedIcon size="S" />
        </ActionButton>
        <span className="rt-sep" />
        <DialogTrigger
          type="popover"
          isOpen={isLinkActive}
          onOpenChange={setIsLinkActive}
        >
          <ActionButton
            isQuiet={!isLinkActive}
            aria-label="Insert link"
            onPress={handleLinkToggle}
          >
            <LinkIcon size="S" />
          </ActionButton>
          <Dialog>
            <Heading>Insert link</Heading>
            <Content>
              <TextField
                label="URL"
                value={linkValue}
                onChange={setLinkValue}
                placeholder="https://example.com"
                width="100%"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && linkValue.trim()) {
                    validateAndApplyLink(linkValue);
                  }
                }}
              />
            </Content>
            <ButtonGroup>
              <Button
                variant="secondary"
                onPress={() => {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                  setLinkValue("");
                  setIsLinkActive(false);
                }}
              >
                Remove
              </Button>
              <Button
                variant="accent"
                onPress={() => {
                  if (linkValue.trim()) {
                    validateAndApplyLink(linkValue);
                  }
                }}
              >
                Apply
              </Button>
            </ButtonGroup>
          </Dialog>
        </DialogTrigger>
        <span className="rt-sep" />
      </Flex>
    </>
  );
}
